/**
 * The one URL scrubber for client error reports, applied to every free-text
 * field by the browser reporter (`src/telemetry/client-errors.ts`) and again by
 * the Pages Function (`functions/api/client-errors.ts`), so a forged or old
 * client cannot bypass it.
 *
 * Every whole URL in the text is rewritten:
 * - same origin as the site → `origin + pathname` only; query and fragment are
 *   dropped (they carry search terms, tokens, ids);
 * - any other origin or scheme (third-party hosts, `chrome-extension://`,
 *   `moz-extension://`, `safari-web-extension://`) → `<external>`, because the
 *   host or extension id alone can identify a visitor's setup;
 * - unparseable → `<url>`.
 * A trailing `:line:col` / `:line` (a stack frame position) is kept in all but
 * the unparseable case. Prose (`why? because`) contains no `scheme://` and is
 * left alone.
 *
 * Plain module with no imports so both the Vite bundle and the Functions build
 * compile it.
 */

const URL_RE = /\b[a-z][a-z0-9+.-]*:\/\/[^\s)"'<>]+/gi;
const POSITION_RE = /(?::\d+){1,2}$/;

export function scrubUrls(text: string, sameOrigins: readonly string[]): string {
  if (typeof text !== "string") return "";
  return text.replace(URL_RE, (match) => {
    let parsed: URL;
    try {
      parsed = new URL(match);
    } catch {
      return "<url>";
    }
    const position = POSITION_RE.exec(match)?.[0] ?? "";
    // A position suffix can only follow a path, query or fragment; without one
    // (`https://host:8080`) the digits are the port and belong to the origin.
    const hasPath = /^[a-z][a-z0-9+.-]*:\/\/[^/?#]*[/?#]/i.test(match);
    const suffix = hasPath ? position : "";
    const web = parsed.protocol === "https:" || parsed.protocol === "http:";
    if (!web || !sameOrigins.includes(parsed.origin)) return `<external>${suffix}`;
    // Re-parse without the suffix so a position glued to the path is not kept twice.
    try {
      const bare = suffix ? new URL(match.slice(0, -suffix.length)) : parsed;
      return `${bare.origin}${bare.pathname}${suffix}`;
    } catch {
      return "<url>";
    }
  });
}
